import React from 'react';
import type { SvgIconProps } from '@mui/material';
import { Tone } from './ComposerParts';

export type VerdictState = 'wait' | 'value' | 'flat';

export interface VerdictHeadProps {
  glyph: React.ComponentType<SvgIconProps>;
  glyphTone?: Tone | '';
  label: React.ReactNode;
  note?: string;
  state: VerdictState;
  waitLabel?: string;
  word?: string;
  tone?: Tone | '';
  sub?: string;
  action?: React.ReactNode;
}

export const VerdictHead: React.FC<VerdictHeadProps> = ({
  glyph,
  glyphTone = '',
  label,
  note,
  state,
  waitLabel = 'Evaluating',
  word = '',
  tone = '',
  sub,
  action,
}) => (
  <div className='psc-bh'>
    <span className={`glyph${glyphTone ? ` psc-tone--${glyphTone}` : ''}`}>
      {React.createElement(glyph, { sx: { fontSize: 15 } })}
    </span>
    <div className={`label${action ? ' has-action' : ''}`}>
      <span className='label-text'>
        {label}
        {note ? <em> — {note}</em> : null}
      </span>
      {action}
    </div>
    {state === 'wait' ? (
      <div className='verdict-row is-waiting'>
        <span className='psc-dot psc-dot--lg psc-pulse' />
        <span className='psc-verdict psc-verdict--wait'>{waitLabel}</span>
      </div>
    ) : (
      <div className='verdict-row'>
        <span
          className={`psc-verdict${
            state === 'flat' ? ' psc-verdict--flat' : ''
          }${tone ? ` psc-tone--${tone}` : ''}`}
        >
          {word}
        </span>
        {sub && <span className='psc-verdict-sub'>{sub}</span>}
      </div>
    )}
  </div>
);

export const BoxTrack: React.FC = () => (
  <div className='psc-track'>
    <span />
  </div>
);

const SKELETON_LINES = ['one', 'two', 'three', 'four'];

export const BoxSkeleton: React.FC<{ lines?: number }> = ({ lines = 3 }) => {
  const shown = SKELETON_LINES.slice(0, lines);
  return (
    <div className='psc-skel-lines' aria-hidden='true'>
      {shown.map((line, index) => (
        <div
          key={line}
          className={`psc-skel psc-skel--line${
            index === shown.length - 1 ? ' psc-skel--short' : ''
          }`}
        />
      ))}
    </div>
  );
};

interface JumpProps {
  onClick: () => void;
  disabled?: boolean;
  glyph?: React.ComponentType<SvgIconProps>;
  glyphFirst?: boolean;
  children: React.ReactNode;
  /** a fuller name, when the visible label repeats another box's; it starts with the label */
  ariaLabel?: string;
}

export const BoxJump: React.FC<JumpProps> = ({
  onClick,
  disabled = false,
  glyph,
  glyphFirst = false,
  ariaLabel,
  children,
}) => (
  <button
    type='button'
    className='psc-jump'
    onClick={onClick}
    disabled={disabled}
    aria-label={ariaLabel}
  >
    {glyph && glyphFirst && React.createElement(glyph, { sx: { fontSize: 13 } })}
    {children}
    {glyph && !glyphFirst && React.createElement(glyph, { sx: { fontSize: 13 } })}
  </button>
);

interface VerdictBoxProps {
  head: VerdictHeadProps;
  foot: React.ReactNode;
  actions?: React.ReactNode;
  children?: React.ReactNode;
}

const VerdictBox: React.FC<VerdictBoxProps> = ({
  head,
  foot,
  actions,
  children,
}) => (
  <div className='psc-box psc-settle'>
    <VerdictHead {...head} />
    {children}
    <div className='psc-box-foot'>
      <span className='tnum'>{foot}</span>
      {actions ? <span className='psc-box-actions'>{actions}</span> : null}
    </div>
  </div>
);

export default VerdictBox;
